import { createApi } from "@reduxjs/toolkit/query/react"
import { FeatureAssignmentThingTypeMrn } from "shared/featureAssignmentData"
import { FeatureThingTypeMrn } from "shared/featureData"
import { baseQuery } from "../baseQuerySl2"

type GetThingsSaResponse = {
  things: any[]
  meta: {
    count: number
    nextToken: string | undefined
  }
}

export const thingsSaApi = createApi({
  reducerPath: "thingsSaApi",
  baseQuery,
  // Use actual thingTypeMrn values as tag types to allow generic invalidation per thing family
  tagTypes: [FeatureThingTypeMrn, FeatureAssignmentThingTypeMrn],
  endpoints: (builder) => ({
    getFeatures: builder.query<
      GetThingsSaResponse,
      { params: Record<string, any> }
    >({
      query: ({ params }) => ({
        url: "things",
        params: {
          ...params,
          "ap.name": "SaGetFeatures",
        },
      }),
      transformResponse: (response: GetThingsSaResponse) => ({
        ...response,
        things: response.things.map((thing) => ({
          ...thing,
          tag: thing.thingTypeMrn,
        })),
      }),
      providesTags: (result) =>
        result?.things
          ? [
              ...result.things.map((f: any) => ({
                type: f.thingTypeMrn || FeatureThingTypeMrn,
                id: f.id,
              })),
              { type: FeatureThingTypeMrn, id: "LIST" },
            ]
          : [{ type: FeatureThingTypeMrn, id: "LIST" }],
    }),
    createFeature: builder.mutation<any, { body: any } | void>({
      query: (arg: any = {}) => ({
        url: "things",
        method: "POST",
        body: arg.body,
      }),
      invalidatesTags: [{ type: FeatureThingTypeMrn, id: "LIST" }],
    }),
    getFeatureById: builder.query<any, { id: string }>({
      query: ({ id }) => ({
        url: "things",
        params: {
          "ap.name": "SaGetFeatureById",
          "ap.id": id,
        },
      }),
      transformResponse: (response: GetThingsSaResponse) =>
        response.things && response.things.length > 0
          ? {
              feature: {
                ...response.things[0],
                tag: response.things[0].thingTypeMrn || FeatureThingTypeMrn,
              },
            }
          : { feature: undefined },
      providesTags: (result, error, arg) =>
        result?.feature
          ? [
              {
                type: result.feature.thingTypeMrn || FeatureThingTypeMrn,
                id: arg.id,
              },
              { type: FeatureThingTypeMrn, id: "LIST" },
            ]
          : [{ type: FeatureThingTypeMrn, id: "LIST" }],
    }),
    getFeatureAssignmentsByTenantId: builder.query<
      GetThingsSaResponse,
      { tenantId: string; params?: Record<string, any> }
    >({
      query: ({ tenantId, params = {} }) => ({
        url: "things",
        params: {
          ...params,
          tenantId,
          "ap.tenantId": tenantId,
          "ap.name": "SaGetFeatureAssignmentsByTenantId",
        },
      }),
    }),
    getFeatureAssignmentsByFeatureId: builder.query<
      GetThingsSaResponse,
      { featureId: string; params?: Record<string, any> }
    >({
      query: ({ featureId, params = {} }) => ({
        url: "things",
        params: {
          ...params,
          "ap.featureId": featureId,
          "ap.name": "SaGetFeatureAssignmentsByFeatureId",
        },
      }),
      providesTags: (result) =>
        result?.things
          ? [
              ...result.things.map((a: any) => ({
                type: a.thingTypeMrn || FeatureAssignmentThingTypeMrn,
                id: a.id,
              })),
              { type: FeatureAssignmentThingTypeMrn, id: "LIST" },
            ]
          : [{ type: FeatureAssignmentThingTypeMrn, id: "LIST" }],
    }),
    getTenants: builder.query<
      GetThingsSaResponse,
      { params?: Record<string, any> }
    >({
      query: ({ params = {} }) => ({
        url: "things",
        params: {
          ...params,
          tenantOnly: true,
          "ap.name": "SaGetTenants",
        },
      }),
    }),
    getTenantById: builder.query<
      GetThingsSaResponse & { tenant?: any },
      { id: string; params?: Record<string, any> }
    >({
      query: ({ id, params = {} }) => ({
        url: "things",
        params: {
          ...params,
          tenantOnly: true,
          "ap.name": "SaGetTenants",
          "ap.ids[0]": id,
        },
      }),
      transformResponse: (response: GetThingsSaResponse) =>
        response.things && response.things.length > 0
          ? {
              ...response,
              tenant: response.things[0],
            }
          : { ...response, tenant: undefined },
    }),
    getTenantsByUserId: builder.query<
      GetThingsSaResponse,
      { userId: string; params?: Record<string, any> }
    >({
      query: ({ userId, params = {} }) => ({
        url: "things",
        params: {
          ...params,
          "ap.name": "SaGetTenantsByUser",
          "ap.userId": userId,
        },
      }),
    }),
    getAdministrators: builder.query<
      GetThingsSaResponse,
      { params?: Record<string, any> }
    >({
      query: ({ params = {} }) => ({
        url: "things",
        params: {
          ...params,
          "ap.name": "SaGetUsers",
          "ap.isSuperAdmin": true,
        },
      }),
    }),
    getAdministratorById: builder.query<
      GetThingsSaResponse & { administrator?: any },
      { id: string; params?: Record<string, any> }
    >({
      query: ({ id, params = {} }) => ({
        url: "things",
        params: {
          ...params,
          "ap.name": "GetItemByKeys",
          "ap.keys": JSON.stringify({
            pk: "mrn::::user",
            sk: `mrn::::user/${id}`,
          }),
        },
      }),
      transformResponse: (response: GetThingsSaResponse) =>
        response.things && response.things.length > 0
          ? {
              ...response,
              administrator: response.things[0],
            }
          : { ...response, administrator: undefined },
    }),

    // Batch endpoints (mirroring thingsApi) for SA context
    createThings: builder.mutation<
      { things: any[]; ids: string[] },
      { body: Record<string, any>; params?: Record<string, any> }
    >({
      query: ({ params = {}, body }) => ({
        url: "things",
        method: "POST",
        params,
        body,
      }),
      // Invalidate list tags for each thingType present in body.things (fallback to both types if unknown)
      invalidatesTags: (result, error, arg) => {
        const things = (arg.body as any)?.things || []
        const typeSet = new Set<string>()
        things.forEach((t: any) => {
          if (t?.thingTypeMrn) typeSet.add(t.thingTypeMrn)
        })
        if (typeSet.size === 0) {
          return [
            {
              type: FeatureThingTypeMrn as typeof FeatureThingTypeMrn,
              id: "LIST",
            },
            {
              type: FeatureAssignmentThingTypeMrn as typeof FeatureAssignmentThingTypeMrn,
              id: "LIST",
            },
          ]
        }
        return Array.from(typeSet)
          .map((tt) => {
            if (tt === FeatureThingTypeMrn) {
              return {
                type: FeatureThingTypeMrn as typeof FeatureThingTypeMrn,
                id: "LIST",
              }
            }
            if (tt === FeatureAssignmentThingTypeMrn) {
              return {
                type: FeatureAssignmentThingTypeMrn as typeof FeatureAssignmentThingTypeMrn,
                id: "LIST",
              }
            }
            // fallback: ignore unknown types
            return undefined
          })
          .filter(Boolean)
      },
    }),
    updateThings: builder.mutation<
      any,
      { body: Record<string, any>; params?: Record<string, any> }
    >({
      query: ({ params = {}, body }) => ({
        url: "things",
        method: "PATCH",
        params,
        body,
      }),
      invalidatesTags: (result, error, arg) => {
        const things = (arg.body as any)?.things || []
        const tags: any[] = []
        things.forEach((t: any) => {
          if (t?.id && t?.thingTypeMrn) {
            tags.push({ type: t.thingTypeMrn, id: t.id })
          }
        })
        if (tags.length === 0) {
          return []
        }
        return tags
      },
    }),
  }),
})

export const {
  useGetFeaturesQuery,
  useCreateFeatureMutation,
  useGetFeatureByIdQuery,
  useGetFeatureAssignmentsByTenantIdQuery,
  useGetFeatureAssignmentsByFeatureIdQuery,
  useGetTenantsQuery,
  useGetTenantByIdQuery,
  useGetTenantsByUserIdQuery,
  useGetAdministratorsQuery,
  useGetAdministratorByIdQuery,
  useCreateThingsMutation: useSaCreateThingsMutation,
  useUpdateThingsMutation: useSaUpdateThingsMutation,
} = thingsSaApi
